'use client';

import React, { useEffect, useState } from 'react';
import { ShoppingCartContextProvider } from './context/ShoppingCartContext';
import { Header } from './components/header';
import { RestaurantOpeningBanner } from './components/restaurantOpeningBanner';
import { DynamicPriceBanner } from './components/dynamicPrice';
import { MobileLayout, DesktopLayout } from './components/layout';
import { useForm, FormProvider } from 'react-hook-form';
import { AddressSelection } from './components/header/AddressSelection';
import { LunchDrinkModal } from './components/lunchDrink/LunchDrinkModal';
import { LunchDrinkUpsellModal } from './components/lunchDrink/LunchDrinkUpsellModal';
import { OverlayLoading } from './components/overlayLoading';
import { PageLoading } from '../loading/PageLoading';
import { useOrder } from '../../state/order/hooks/useOrder';
import { useUser } from '../../hooks/useUser';
import { useCheckOrder } from '../../state/order/hooks/useOrderMutation';
import { useClearOldOrder } from '../../state/order/hooks/useClearOldOrder';
import { CrossSellUpsellModal } from './components/crossSell/CrossSellUpsellModal';
import { useMediaQuery } from 'react-responsive';
import { breakpoint, mediaQueryMin } from '../../theme';
import { colors } from '../../theme/color';
import type {
  SbIconListItem,
  SbUpsellBox,
  SbComponent,
  SbComponentBase,
  SbDeliveryWithoutPackaging,
  SbProductIdSelector,
} from '../storyblok/types';
import styled from 'styled-components';
import { analyticsEvents } from '../../utils/analytics/analytics';
import { contextType, Snackbar } from '../snackbar';
import { usePersonalDiscounts } from '../../state/campaign/hooks/usePersonalDiscounts';
import { ModalQueryParamOpener } from '../modal/ModalQueryParamOpener';

export type SbShoppingCartData = SbComponentBase & {
  content_slices: SbComponent[];
  delivery_without_packaging?: SbDeliveryWithoutPackaging[];
  alhocol_upsell_logged_in_and_adult?: SbUpsellBox[];
  alcohol_upsell_guest?: SbUpsellBox[];
  alcohol_upsell_restrictions?: SbIconListItem[];
  alcohol_ordering_info_link?: {
    url: string;
  };
  alcohol_ordering_info_link_label?: string;
  drink_upsell?: SbProductIdSelector[];
  snack_upsell?: SbProductIdSelector[];
  global_campaign_slug?: string;
  global_campaign_slug_drinks?: string;
  global_campaign_activate_text?: string;
  global_drink_campaign_activate_text?: string;
  global_campaign_enabled_text?: string;
  global_campaign_disabled_text?: string;
  global_drink_campaign_enabled_text?: string;
  global_drink_campaign_disabled_text?: string;
  /** URL of the campaign terms page. Empty or absent hides the terms link. */
  global_campaign_terms?: LinkUrl;
  service_fee_info?: string;
};

type LinkUrl = {
  url: string;
  cached_url: string;
};

const ShoppingCart: React.FC = () => {
  const order = useOrder();
  const { latestDiscount, refresh } = usePersonalDiscounts();
  const [pageOpenedAt] = useState(() => Date.now());

  const isMobile = useMediaQuery({ query: `(max-width: ${breakpoint.medium}px)` });

  useClearOldOrder();

  // Always update personal discounts cache when landing on cart page.
  useEffect(() => {
    refresh();
  }, [refresh]);

  const validateOrder = useCheckOrder();
  const { isUserLoading } = useUser();

  useEffect(() => {
    analyticsEvents.beginCheckout(order);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void (async (): Promise<void> => {
      try {
        await validateOrder();
      } catch {
        // Errors are already surfaced via OrderProcessor's addError/trackInsightsException.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.restaurantId, order.type]);

  const methods = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const onSubmit = (): void => {
    return;
  };

  const isLatestDiscountFresh =
    !!latestDiscount &&
    typeof latestDiscount.createdAt === 'string' &&
    pageOpenedAt - new Date(latestDiscount.createdAt).getTime() < 15 * 60 * 1000;

  const isLatestDiscountOnCart = order.promotionCodes.includes(latestDiscount?.code || '');

  return (
    <div>
      {isUserLoading ? (
        <PageLoading />
      ) : (
        <>
          <StyledMain id="main-content">
            <ShoppingCartContextProvider>
              <ModalQueryParamOpener />
              <AddressSelection />
              <LunchDrinkUpsellModal />
              <CrossSellUpsellModal />
              <LunchDrinkModal />
              <DynamicPriceBanner />
              {isLatestDiscountFresh && !isLatestDiscountOnCart && (
                <Snackbar
                  tight
                  id={`code_${latestDiscount.code}`}
                  align="center"
                  message={`[**${latestDiscount.discountName}** lisättiin juuri etukoodeihisi!](#edut)`}
                  context={contextType.NOTIFICATION}
                />
              )}
              <RestaurantOpeningBanner />
              <Header />
              {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
              {/* @ts-ignore */}
              <FormProvider {...methods}>
                <form onSubmit={methods.handleSubmit(onSubmit)}>
                  {isMobile ? <MobileLayout /> : <DesktopLayout />}
                </form>
              </FormProvider>
              <OverlayLoading />
            </ShoppingCartContextProvider>
          </StyledMain>
        </>
      )}
    </div>
  );
};

export default ShoppingCart;

const StyledMain = styled.main`
  background: ${colors.dough.lighter(80)};

  @media ${mediaQueryMin.large} {
    padding-bottom: 80px;
  }
`;
